import { useState, useEffect, useCallback, useRef } from 'react';

// Configuration
const IDLE_TIMEOUT = 1200; // seconds (20 minutes)
const IDLE_ALERT_TIME = 120; // seconds (2 minutes before timeout)
const LOCAL_STORAGE_KEY = 'global_countdown_last_reset_timestamp';

// Define the shape of the object returned by the hook
interface SessionTimerHook {
    alertRemaining: number | null;
    resetTimer: () => void;
}

/**
 * Custom hook to handle idle session monitoring and alerting.
 * @param {() => void} onLogout - Callback function to execute when the session times out.
 * @param {boolean} isLoggedIn - Parameter to control if the timer should be active.
 * @returns {SessionTimerHook}
 */
export function useIdleSessionTimer(onLogout: () => void, isLoggedIn: boolean): SessionTimerHook {
    // alertRemaining: number | null. null means no alert is currently needed/active.
    const [alertRemaining, setAlertRemaining] = useState<number | null>(null);

    // timerRef: NodeJS.Timeout | null. This holds the interval ID.
    const timerRef = useRef<number | null>(null);

    // onLogoutRef: React.MutableRefObject<() => void>. Used to keep the latest function reference without dependency array issues.
    const onLogoutRef = useRef(onLogout);

    // Update ref when onLogout changes
    useEffect(() => {
        onLogoutRef.current = onLogout;
    }, [onLogout]);

    // --- Utility Functions ---

    const getLocalStorage = useCallback((): Storage | null => {
        try {
            // Check if window (and thus localStorage) is available (client-side)
            return typeof window !== 'undefined' ? window.localStorage : null;
        } catch (e) {
            return null;
        }
    }, []);

    const getLastResetTimestamp = useCallback((): number => {
        const storage = getLocalStorage();
        if (storage) {
            const storedTime = parseInt(storage[LOCAL_STORAGE_KEY] || '', 10);
            // Fallback to current time if invalid or not found
            return !isNaN(storedTime) && storedTime > 0 ? storedTime : Date.now();
        }
        return Date.now();
    }, [getLocalStorage]);

    const setLastResetTimestamp = useCallback((timeStamp: number): void => {
        const storage = getLocalStorage();
        if (storage) {
            storage[LOCAL_STORAGE_KEY] = timeStamp.toString();
        }
    }, [getLocalStorage]);

    // --- Core Logic ---

    const resetTimer = useCallback((): void => {
        // Only reset time if we are actually logged in
        if (!isLoggedIn) return;
        setLastResetTimestamp(Date.now());
        setAlertRemaining(null); // Closes the alert
    }, [setLastResetTimestamp, isLoggedIn]);

    const checkIdleTime = useCallback((): void => {
        // Stop immediately if not logged in
        if (!isLoggedIn) {
            setAlertRemaining(null);
            return;
        }

        const currentTimeStamp = Date.now();
        const lastResetTimeStamp = getLastResetTimestamp();
        const secondsDiff = Math.floor((currentTimeStamp - lastResetTimeStamp) / 1000);

        // Sanity check
        if (secondsDiff < 0) {
            resetTimer();
            return;
        }

        const remaining = IDLE_TIMEOUT - secondsDiff;

        //console.log(`Idle session timer check: ${secondsDiff} seconds idle, ${remaining} seconds remaining.`);

        if (remaining <= 0) {
            // Session Timeout - Logout
            if (timerRef.current) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
                console.log("Idle session timer stopped due to timeout.");
            }
            resetTimer(); // Reset time/storage before logging out
            onLogoutRef.current();
        } else if (remaining <= IDLE_ALERT_TIME) {
            // Alert Window
            setAlertRemaining(remaining);
        } else {
            // Normal Idle
            setAlertRemaining(null);
        }
    }, [getLastResetTimestamp, resetTimer, isLoggedIn]);

    // --- Side Effects (Lifecycle) ---

    useEffect(() => {
        if (isLoggedIn) {
            // 1. Initialize Interval
            resetTimer(); // Ensure timer is reset on login

            // Using window.setInterval ensures we get the browser's timer ID, compatible with clearInterval
            timerRef.current = window.setInterval(checkIdleTime, 1000);

            console.log(`Idle session timer started. Timeout in ${IDLE_TIMEOUT} seconds.`);

            // 2. Attach Global Reset Events
            const events: (keyof DocumentEventMap)[] = ['click', 'mousemove', 'keypress', 'load'];

            events.forEach(event => {
                document.addEventListener(event, resetTimer, { passive: true });
            });

            // 3. Cleanup Function
            return () => {
                if (timerRef.current) {
                    window.clearInterval(timerRef.current);
                    timerRef.current = null;
                    console.log("Idle session timer stopped.");
                }
                events.forEach(event => {
                    document.removeEventListener(event, resetTimer);
                });
                // Important: Reset alert when logging out or component unmounts
                setAlertRemaining(null);
            };
        }
        else {
            // If not logged in, ensure the timer is cleared immediately
            if (timerRef.current) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
                console.log("Idle session timer stopped as user is not logged in.");
            }
            setAlertRemaining(null); // Ensure no alert shows when logged out
        }
    }, [checkIdleTime, resetTimer, isLoggedIn]);

    return {
        alertRemaining,
        resetTimer
    };
}